'use client';

import { ArrowLeft, ArrowRight } from '@/components/ui/Icons';
import React from 'react';
import { useTranslations } from 'next-intl';

interface PaginationProps {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number | string;
  onPageChange?: (params: Record<string, any>) => void;
  offset?: number;
  showGoTo?: boolean;
  syncWithSSRUrl?: boolean;
  searchLocation?: any;
}

const Pagination: React.FC<PaginationProps> = ({
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
  offset = 2,
  showGoTo = true,
  syncWithSSRUrl = false,
  searchLocation = window.location.search,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  // const visiblePages = Math.min(totalPages, offset * 2 + 1);

  const pages: number[] = [];
  const t = useTranslations();

  const searchParams = new URLSearchParams(searchLocation);

  const pageNum = searchParams.get('page');

  let activePage = 1;

  if (syncWithSSRUrl && currentPage && typeof currentPage === 'string') {
    activePage = parseInt(currentPage);
  } else if (syncWithSSRUrl && currentPage) {
    activePage = currentPage as number;
  } else if (typeof pageNum === 'string') {
    activePage = parseInt(pageNum);
  }

  const startPage = Math.max(1, activePage - offset);
  const endPage = Math.min(totalPages, activePage + offset);

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  const handlePageChange = (params: Record<string, any>) => {
    if (
      params &&
      (('page' in params && params?.page >= 1 && params?.page <= totalPages) ||
        'per_page' in params)
    ) {
      if (onPageChange) {
        onPageChange(params);
      } else {
        // TODO: handle if onPageChange not passed
      }
    }
  };

  return (
    <>
      <div className="mt-4 flex w-full flex-col items-center space-x-0 space-y-3 sm:flex-row sm:items-center sm:space-x-3 sm:space-y-0">
        <div className="flex items-center justify-center">
          <button
            onClick={() => handlePageChange({ page: activePage - 1 })}
            disabled={activePage === 1}
            className="border border-gray-200 bg-white p-[10px] text-gray-400 disabled:bg-gray-200"
          >
            <span className="flex h-3 w-4 items-center justify-center sm:h-[21px] sm:w-[21px]">
              <ArrowLeft />
            </span>
          </button>
          <ul className="flex items-center justify-center">
            {startPage > 1 && (
              <>
                <li>
                  <button
                    onClick={() => handlePageChange({ page: 1 })}
                    className="border border-gray-200 bg-white p-[10px]"
                  >
                    <span className="flex h-3 w-4 items-center justify-center text-base text-paragraph sm:h-[21px] sm:w-[26px]">
                      1
                    </span>
                  </button>
                </li>
                {startPage > 2 && (
                  <li className="px-2">
                    <button
                      onClick={() =>
                        handlePageChange({ page: startPage - offset - 1 })
                      }
                      className="flex h-3 w-4 items-center justify-center text-base text-paragraph sm:h-[21px] sm:w-[26px]"
                    >
                      ...
                    </button>
                  </li>
                )}
              </>
            )}
            {pages.map((page) => (
              <li key={page}>
                <button
                  onClick={() => handlePageChange({ page })}
                  className={`border border-gray-200 p-[10px] ${
                    page === activePage
                      ? 'bg-primary text-white'
                      : 'bg-white text-paragraph'
                  }`}
                >
                  <span className="flex h-3 w-4 items-center justify-center text-base sm:h-[21px] sm:w-[26px]">
                    {page}
                  </span>
                </button>
              </li>
            ))}
            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && (
                  <li className="px-2">
                    <button
                      onClick={() =>
                        handlePageChange({ page: endPage + offset + 1 })
                      }
                      className="flex h-3 w-4 items-center justify-center text-base text-paragraph sm:h-[21px] sm:w-[26px]"
                    >
                      ...
                    </button>
                  </li>
                )}
                <li>
                  <button
                    onClick={() => handlePageChange({ page: totalPages })}
                    className="border border-gray-200 bg-white p-[10px]"
                  >
                    <span className="flex h-3 w-4 items-center justify-center text-base text-paragraph sm:h-[21px] sm:w-[26px]">
                      {totalPages}
                    </span>
                  </button>
                </li>
              </>
            )}
          </ul>
          <button
            onClick={() => handlePageChange({ page: activePage + 1 })}
            disabled={activePage === totalPages}
            className="border border-gray-200 bg-white p-[10px] text-paragraph disabled:bg-gray-200"
          >
            <span className="flex h-3 w-4 items-center justify-center sm:h-[21px] sm:w-[21px]">
              <ArrowRight />
            </span>
          </button>
        </div>
        {showGoTo && (
          <div className="flex flex-col items-center space-y-3 sm:flex-row sm:space-x-3 sm:space-y-0">
            <div className="flex items-center gap-x-2">
              <span>{t('res_panel.go_to_page')}:</span>
              <input
                type="number"
                value={activePage}
                onChange={(e) => {
                  const page = e.target.value ? Number(e.target.value) : 1;
                  handlePageChange({ page });
                }}
                className="w-16 rounded border border-gray-300 px-2 py-1.5 text-center"
                min={0}
              />
            </div>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                handlePageChange({ per_page: Number(e.target.value) });
              }}
              className="w-[120px] rounded border border-gray-300 px-2 py-1.5"
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </>
  );
};

export default Pagination;
